import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Stethoscope, CheckCircle2, XCircle, Wrench } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin diagnostic: shows every gate that can block an employee's portal
// (email verification, onboarding, status...) with one-click fixes.
export const PortalHealthCard = ({ employeeId }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/employees/${employeeId}/portal-health`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Health check failed');
    } finally {
      setLoading(false);
    }
  };

  const fix = async (key) => {
    try {
      if (key === 'email_verified') {
        await axios.post(`${API}/admin/employees/${employeeId}/force-verify-email`);
        toast.success('Email marked as verified — portal unblocked');
      } else if (key === 'onboarding_complete') {
        await axios.post(`${API}/admin/employees/${employeeId}/onboarding/complete`, {
          reason: 'Unblocked via Portal Health check',
        });
        toast.success('Onboarding marked complete — portal unblocked');
      }
      await run();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix failed');
    }
  };

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="portal-health-card">
      <CardHeader className="border-b border-border pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-primary" strokeWidth={1.5} /> Portal Health
        </CardTitle>
        <Button size="sm" variant="outline" className="rounded-sm text-xs uppercase" onClick={run} disabled={loading} data-testid="run-health-check-btn">
          {loading ? 'Checking…' : report ? 'Re-check' : 'Run check'}
        </Button>
      </CardHeader>
      {report && (
        <CardContent className="p-4 space-y-2">
          {report.blocked && (
            <p className="text-sm font-semibold text-red-400 border border-red-500/50 bg-red-950/40 rounded-sm px-3 py-2" data-testid="portal-blocked-warning">
              ⛔ This employee's portal is currently BLOCKED — fix the failing checks below.
            </p>
          )}
          {report.checks.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-3 border-b border-border/50 pb-2" data-testid={`health-${c.key}`}>
              <div className="flex items-start gap-2 min-w-0">
                {c.ok
                  ? <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm">{c.label}</p>
                  {c.detail && <p className="text-xs text-muted-foreground">{c.detail}</p>}
                </div>
              </div>
              {!c.ok && c.fixable && (
                <Button size="sm" className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 text-xs uppercase shrink-0" onClick={() => fix(c.key)} data-testid={`fix-${c.key}-btn`}>
                  <Wrench className="w-3.5 h-3.5 mr-1" /> Fix
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

export default PortalHealthCard;
